import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import {Settings, Save} from 'lucide-react'

interface PointsSetting {
  _id: string
  settingKey: string
  pointsPerDollar: number
  minPointsToUse: number
  isActive: boolean
  description?: string
  createdAt: string
  updatedAt: string
}

const PointsSettingsManager: React.FC = () => {
  const [setting, setSetting] = useState<PointsSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  
  // 表單狀態
  const [pointsPerDollar, setPointsPerDollar] = useState(100)
  const [minPointsToUse, setMinPointsToUse] = useState(100)
  const [isActive, setIsActive] = useState(true)
  const [description, setDescription] = useState("")

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.points_settings.list({
        filter: { settingKey: "default" }
      })
      
      if (list && list.length > 0) {
        const currentSetting = list[0]
        setSetting(currentSetting)
        setPointsPerDollar(currentSetting.pointsPerDollar)
        setMinPointsToUse(currentSetting.minPointsToUse)
        setIsActive(currentSetting.isActive)
        setDescription(currentSetting.description || "")
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error)
      alert("❌ 載入設定失敗")
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    // 驗證
    if (pointsPerDollar <= 0) {
      alert("⚠️ 點數兌換比例必須大於0")
      return
    }
    if (minPointsToUse < 0) {
      alert("⚠️ 最低使用點數不能為負數")
      return
    }

    setSaving(true)
    try {
      const now = new Date().toISOString()
      const data = {
        pointsPerDollar,
        minPointsToUse,
        isActive,
        description: description.trim() || `點數兌換規則：${pointsPerDollar}分=1元，最低使用${minPointsToUse}分`,
        updatedAt: now
      }

      if (setting) {
        // 更新現有設定
        await lumi.entities.points_settings.update(setting._id, data)
        alert("✅ 設定已更新")
      } else {
        // 創建新設定
        await lumi.entities.points_settings.create({
          ...data,
          settingKey: "default",
          createdAt: now
        })
        alert("✅ 設定已創建")
      }
      
      await fetchSettings()
    } catch (error) {
      console.error("保存失敗：", error)
      alert(`❌ 保存失敗：${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">載入中...</div>
      </div>
    )
  }

  // 計算示例
  const examplePoints = 1000
  const exampleDollar = examplePoints / pointsPerDollar

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-light tracking-wider mb-2">點數折扣設定</h2>
          <p className="text-gray-400">配置會員點數兌換比例與使用規則</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          <Save size={20} />
          {saving ? "保存中..." : "保存設定"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 左側：設定表單 */}
        <div className="space-y-6">
          <div className="bg-gray-950 border border-gray-800 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings size={24} className="text-blue-400" />
              <h3 className="text-xl font-light tracking-wider">基本設定</h3>
            </div>

            <div className="space-y-6">
              {/* 啟用狀態 */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="w-5 h-5 bg-gray-900 border border-gray-700 checked:bg-blue-600 checked:border-blue-600 cursor-pointer"
                  />
                  <div>
                    <div className="text-gray-100 font-medium">啟用點數折扣功能</div>
                    <div className="text-xs text-gray-500">關閉後會員無法在結帳時使用點數</div>
                  </div>
                </label>
              </div>

              {/* 點數兌換比例 */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  點數兌換比例 <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={pointsPerDollar}
                    onChange={(e) => setPointsPerDollar(Number(e.target.value))}
                    min="1"
                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-gray-400 whitespace-nowrap">分 = NT$ 1</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  例如：設定為 100，表示 100 點 = NT$ 1 元
                </div>
              </div>

              {/* 最低使用點數 */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  最低使用點數門檻 <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={minPointsToUse}
                    onChange={(e) => setMinPointsToUse(Number(e.target.value))}
                    min="0"
                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-gray-400">分</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  會員至少需要此數量的點數才能使用折扣
                </div>
              </div>

              {/* 說明 */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  設定說明（選填）
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="輸入此設定的說明或備註..."
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500 resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* 右側：使用說明與示例 */}
        <div className="space-y-6">
          {/* 當前設定摘要 */}
          <div className="bg-gray-950 border border-gray-800 p-6">
            <h3 className="text-xl font-light tracking-wider mb-4">當前設定摘要</h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-800">
                <span className="text-gray-400">功能狀態</span>
                <span className={`font-medium ${isActive ? 'text-green-400' : 'text-red-400'}`}>
                  {isActive ? '✓ 已啟用' : '✕ 已停用'}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-800">
                <span className="text-gray-400">兌換比例</span>
                <span className="text-gray-100">{pointsPerDollar} 分 = NT$ 1</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-400">最低使用</span>
                <span className="text-gray-100">{minPointsToUse} 分</span>
              </div>
            </div>
          </div>

          {/* 使用示例 */}
          <div className="bg-blue-950 border border-blue-800 p-6">
            <h3 className="text-xl font-light tracking-wider mb-4 text-blue-100">使用示例</h3>
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-blue-200 mb-2">📊 點數價值計算：</div>
                <div className="bg-blue-900 bg-opacity-30 p-3 rounded">
                  <div className="text-blue-100">
                    會員有 <span className="font-bold">{examplePoints.toLocaleString()}</span> 點
                  </div>
                  <div className="text-blue-300 text-xs mt-1">
                    = NT$ <span className="font-bold">{exampleDollar.toLocaleString()}</span> 元折扣
                  </div>
                </div>
              </div>

              <div>
                <div className="text-blue-200 mb-2">🛒 實際訂單範例：</div>
                <div className="bg-blue-900 bg-opacity-30 p-3 rounded space-y-2">
                  <div className="text-blue-100">
                    會員擁有 1000 點，可全額折抵 NT$ 10 元
                  </div>
                  <div className="text-blue-300 text-xs mt-1">
                    購買 NT$ 500 商品時，可選擇使用任意點數折抵
                  </div>
                  <div className="text-blue-400 text-xs mt-2 pt-2 border-t border-blue-700">
                    ✓ 無折扣上限，會員可自由決定使用點數數量
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 注意事項 */}
          <div className="bg-gray-950 border border-gray-800 p-6">
            <h3 className="text-xl font-light tracking-wider mb-4">⚠️ 注意事項</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-gray-600">•</span>
                <span>修改設定後會立即生效，影響所有新訂單</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-600">•</span>
                <span>已使用的點數不可退回，請謹慎設定兌換比例</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-600">•</span>
                <span>會員可使用全部點數折抵，無折扣上限限制</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-600">•</span>
                <span>停用功能後，會員仍可累積點數但無法使用</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PointsSettingsManager
